import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, X, ZoomIn } from "lucide-react";

import preislistePreview from "@/assets/optimized/preisliste-preview.webp";
import preislisteImg from "@/assets/preisliste.png";
import SectionHeader from "@/components/SectionHeader";

const pricingNotes = [
  "Direkte Abholung vor Ort in Nalbach",
  "Abrechnung transparent nach Kubikmeter",
];

const PreislisteSection = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    const originalOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <section id="preise" className="section-shell-tight overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] lg:gap-16">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <SectionHeader
              eyebrow="Preise"
              title="Klar kalkulierbar."
              description="Die Preisliste ist direkt einsehbar. Für eine genaue Prüfung können Sie die Grafik vergrößern."
            />

            <div className="space-y-4 border-t border-white/10 pt-6">
              {pricingNotes.map((note) => (
                <div key={note} className="flex items-start gap-4">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary" />
                  <p className="text-sm leading-relaxed text-muted-foreground md:text-base">{note}</p>
                </div>
              ))}
            </div>

            <p className="mt-8 text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Stand: September 2025 · Änderungen vorbehalten
            </p>
          </div>

          <motion.button
            type="button"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6 }}
            onClick={() => setOpen(true)}
            className="group surface-panel-strong relative overflow-hidden p-3 text-left"
            aria-expanded={open}
            aria-haspopup="dialog"
          >
            <div className="relative overflow-hidden rounded-[calc(var(--radius)+2px)]">
              <img
                src={preislistePreview}
                alt="BETON2GO Preisliste – Abholpreise pro Kubikmeter"
                width={1200}
                height={518}
                loading="lazy"
                decoding="async"
                sizes="(min-width: 1024px) 55vw, 100vw"
                className="h-auto w-full transition-transform duration-500 group-hover:scale-[1.018]"
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent,rgba(0,0,0,0.18))]" />
            </div>

            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6">
              <div>
                <p className="info-label text-primary/80">Zum Vergrößern öffnen</p>
                <p className="mt-2 font-display text-2xl uppercase tracking-[0.04em] text-foreground">
                  Preisliste ansehen
                </p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-full border border-white/14 bg-background/55 backdrop-blur-md">
                <ZoomIn className="h-5 w-5 text-primary" />
              </div>
            </div>
          </motion.button>
        </div>
      </div>

      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-stretch justify-center bg-background/92 p-0 backdrop-blur-md sm:items-center sm:p-4"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 18 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.3 }}
              className="relative flex h-[100svh] w-screen flex-col overflow-hidden bg-card shadow-[0_32px_90px_rgba(0,0,0,0.45)] sm:h-auto sm:max-h-[92vh] sm:max-w-[96vw] sm:rounded-[calc(var(--radius)+8px)] sm:border sm:border-white/12 sm:p-3"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="BETON2GO Preisliste"
            >
              <div className="flex items-start justify-between gap-4 border-b border-white/10 px-4 py-4 sm:border-0 sm:px-2 sm:pb-3 sm:pt-2">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    BETON2GO Preisliste
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground sm:hidden">
                    Vollbildansicht. Für Details einfach seitlich wischen.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/12 bg-background/72 text-foreground transition-colors hover:bg-background"
                  aria-label="Preisliste schließen"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-auto overscroll-contain px-4 py-4 sm:px-0 sm:py-0">
                <div className="flex h-full w-max min-w-full items-center justify-start sm:justify-center">
                  <img
                    src={preislisteImg}
                    alt="BETON2GO Preisliste – Abholpreise pro Kubikmeter"
                    width={1606}
                    height={693}
                    decoding="async"
                    className="h-full w-auto max-w-none rounded-[calc(var(--radius)+2px)] object-contain sm:h-auto sm:max-h-[86vh] sm:max-w-full sm:object-contain"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 px-4 py-3 sm:mt-4 sm:flex-nowrap sm:border-0 sm:px-2 sm:pb-1">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  Nahansicht auf Mobilgeräten
                </p>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary sm:text-right">
                  <ArrowUpRight className="h-4 w-4" />
                  Horizontal scrollen
                </div>
              </div>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </section>
  );
};

export default PreislisteSection;
